import React from "react";
import { motion } from "framer-motion";
import { PhoneCall, Search, Truck, FileCheck } from "lucide-react";
import { STEPS } from "@/constants/testIds";

const steps = [
  {
    id: "brief",
    n: "01",
    icon: PhoneCall,
    title: "Tell us about the load",
    desc:
      "Send us your pickup, drop, dimensions and dates by phone, email or WhatsApp. No portal, no logins, no paperwork.",
  },
  {
    id: "match",
    n: "02",
    icon: Search,
    title: "We find the right carrier",
    desc:
      "We match your load against our vetted carrier network — picking on lane, rate, equipment and proven reliability.",
  },
  {
    id: "move",
    n: "03",
    icon: Truck,
    title: "We coordinate the move",
    desc:
      "Booking, paperwork, border docs, comms with the driver, status updates to you. One contact through the whole move.",
  },
  {
    id: "close",
    n: "04",
    icon: FileCheck,
    title: "POD & single invoice",
    desc:
      "Delivery confirmed, POD captured, paperwork filed. You get one clean invoice — even for multi-leg moves.",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="process"
      data-testid={STEPS.section}
      className="relative py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs md:text-sm uppercase tracking-[0.24em] font-bold text-[#0070C0]">
            Our process
          </div>
          <h2 className="font-display mt-4 text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#002050] leading-[1.05]">
            How a load moves with us.
          </h2>
          <p className="mt-5 text-lg text-[#1F2937]/75 leading-relaxed">
            Four simple steps. The first one starts with you sending us a brief —
            the rest happens behind the scenes.
          </p>
        </div>

        <div className="mt-16 md:mt-20 relative">
          {/* Connection line - sits behind circle nodes */}
          <div className="hidden lg:block absolute -top-[2px] left-[10%] right-[10%] z-0 pointer-events-none">
            <div className="border-t-2 border-dashed border-[#00AEEF]/50" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 relative">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={s.id}
                  data-testid={STEPS.step(s.id)}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative bg-white rounded-2xl border border-[#E2E8F0] p-7 md:p-8 hover:border-[#0070C0]/40 hover:shadow-[0_20px_50px_-25px_rgba(0,32,80,0.3)] transition-all"
                >
                  <div className="relative -mt-16 mb-4 flex justify-start">
                    <div className="relative">
                      <div className="absolute -inset-2 rounded-full bg-gradient-to-tr from-[#0070C0] to-[#00AEEF] blur-md opacity-50" />
                      <div className="relative h-16 w-16 rounded-full bg-[#002050] border-4 border-white shadow-[0_8px_24px_rgba(0,32,80,0.3)] flex items-center justify-center">
                        <Icon className="h-7 w-7 text-[#00AEEF]" strokeWidth={1.75} />
                      </div>
                    </div>
                  </div>
                  <div className="font-display text-xs font-bold uppercase tracking-[0.2em] text-[#0070C0]">
                    Step {s.n}
                  </div>
                  <h3 className="font-display mt-2 text-2xl font-bold text-[#002050] tracking-tight">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-[15px] text-[#1F2937]/75 leading-relaxed">
                    {s.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
